import { QueryResult, gql } from '@apollo/client';
import React, { ReactNode, useMemo } from 'react';
import { useUserContext } from '../contexts';
import { ACCOUNT } from '../graphql/fragments/Account';
import { AllAccountsQuery } from '../graphql/types';
import { useQueryAndRefetchOnUserChange } from '../lib/hooks';

export const ALL_ACCOUNTS = gql`
  query AllAccounts {
    accounts {
      edges {
        ...Account
      }
    }
  }
  ${ACCOUNT}
`;

type Account = AllAccountsQuery['accounts']['edges'][number];

export function findAccountByAddress(
  accounts: Iterable<Account>,
  integrationId: string,
  address: string | null | undefined,
) {
  if (!address) return undefined;
  for (const account of accounts) {
    if (account.integrationId === integrationId && account.address === address) return account;
  }
  return undefined;
}

type AccountsQueryResult = Omit<QueryResult<AllAccountsQuery>, 'data'> & {
  data: Account[] | undefined;
  accountsById: Map<number, Account>;
};

const AccountsContext = React.createContext<AccountsQueryResult | undefined>(undefined);

interface Props {
  children: ReactNode;
}

export default function AccountsProvider({ children }: Props) {
  const { isLoggedIn } = useUserContext();
  const res = useQueryAndRefetchOnUserChange<AllAccountsQuery>(ALL_ACCOUNTS, {
    fetchPolicy: 'cache-and-network',
    skip: !isLoggedIn,
  });

  const accountsById = useMemo(
    () => new Map(res.data?.accounts.edges.map((account) => [account.id, account]) ?? []),
    [res.data],
  );

  const value = useMemo(
    () => ({
      ...res,
      data: res.data?.accounts.edges,
      accountsById,
    }),
    [res, accountsById],
  );

  return <AccountsContext.Provider value={value}>{children}</AccountsContext.Provider>;
}

export function useAllAccounts() {
  const context = React.useContext(AccountsContext);
  if (!context) {
    throw new Error('useAllAccounts must be used within AccountsProvider');
  }
  return context;
}
